'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import NavBar from '@/components/navbar'
import PageTitle from '@/components/page-title'
import OfflineBanner from '@/components/offline-banner'
import Tournaments from '@/components/tournaments'
import WipPage from '@/components/wip'
import { initTournament } from '@/components/utils/apiHelper'
import '../index.css'
import '@/app/globals.css'
import SunMoon from '@/components/utils/sun-moon'
import SyncIndicator from '@/components/utils/sync-indicator'
import AxelerantLogo from '@/components/utils/axl-logo'
import { Analytics, GA_ID } from '@/components/utils/analytics'
import '@fortawesome/fontawesome-svg-core/styles.css'
import { config } from '@fortawesome/fontawesome-svg-core'
config.autoAddCss = false

const isWipMode = process.env.NEXT_PUBLIC_WIP_MODE === 'true'

export default function RootLayout({ children }) {
  const pathname = usePathname()
  // Routes that opt out of the global chrome (header, nav, footer) and
  // render as bare full-viewport landing pages.
  const isImmersive = pathname?.startsWith('/recap') || pathname?.startsWith('/awards')

  const [online, setOnline] = useState(true)
  const [tournamentName, setTournamentName] = useState('')
  const [colorMode, setColorMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('darkModeCheckbox');
      if (stored === 'light') return true;
      if (stored === 'dark') return false;
      // No explicit preference — use system default
      return window.matchMedia('(prefers-color-scheme: light)').matches;
    }
    return false;
  })

  useEffect(() => {
    const handleOnline = () => setOnline(true)
    const handleOffline = () => setOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  useEffect(() => {
    const handleColorModeChanged = (event) => {
      setColorMode(!!event.detail);
    }
    document.addEventListener('colorModeChanged', handleColorModeChanged)
    const handleStorage = (event) => {
      if (event.key === 'darkModeCheckbox') {
        setColorMode(event.newValue === 'light');
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => {
      document.removeEventListener('colorModeChanged', handleColorModeChanged)
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  useEffect(() => {
    // Fetch active tournament from Supabase on mount
    const init = async () => {
      const tournament = await initTournament();
      if (tournament) {
        setTournamentName(tournament.name);
      }
    };
    init();
  }, []);

  useEffect(() => {
    // Anchor mobile tooltips near their trigger by setting CSS variables
    // (--tt-top / --tt-bottom) when the trigger is hovered, focused, or
    // tapped. Pure CSS can't read a trigger's viewport coordinates, so
    // this tiny delegate computes them on demand.
    const positionTooltip = (event) => {
      const trigger = event.target.closest?.('.has-tooltip[data-tooltip]');
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const vh = window.innerHeight;
      const spaceAbove = rect.top;
      const spaceBelow = vh - rect.bottom;
      // Prefer above; fall back to below when the trigger is near the top
      if (spaceAbove >= 96 || spaceAbove >= spaceBelow) {
        trigger.style.setProperty('--tt-bottom', `${vh - rect.top + 8}px`);
        trigger.style.setProperty('--tt-top', 'auto');
      } else {
        trigger.style.setProperty('--tt-top', `${rect.bottom + 8}px`);
        trigger.style.setProperty('--tt-bottom', 'auto');
      }
    };
    document.addEventListener('pointerover', positionTooltip, true);
    document.addEventListener('focusin', positionTooltip, true);
    return () => {
      document.removeEventListener('pointerover', positionTooltip, true);
      document.removeEventListener('focusin', positionTooltip, true);
    };
  }, []);

  return (
    <html lang="en">
      <head>
        {/* Microsoft Clarity */}
        <script dangerouslySetInnerHTML={{__html: `
          (function(c,l,a,r,i,t,y){
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
            t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
            y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "w7f1f1tpke");
        `}} />
        <link rel="preconnect" href="https://www.google-analytics.com" />
        {/* Google tag (gtag.js) */}
        <script async src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`}></script>
        <script dangerouslySetInnerHTML={{__html: `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${GA_ID}');
        `}} />
        <meta charSet="utf-8" />
        <link rel="icon" href="/favicon.webp" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#462255" />
        {/* Primary Meta Tags */}
        <title>IPL 2026 | Axelerant Prediction Pro</title>
        <meta name="title" content="IPL 2026 | Axelerant Prediction Pro" />
        <meta name="description" content="Axelerant IPL Prediction Pro is the prediction game for the T20 cricket league (Indian Premier League) exclusively designed for cricket fans at Axelerant and to engage them with the sport they love deeply." />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://iplpredictionpro.axelerant.com/" />
        <meta property="og:title" content="IPL 2026 | Axelerant Prediction Pro" />
        <meta property="og:description" content="Axelerant IPL Prediction Pro is the prediction game for the T20 cricket league (Indian Premier League) exclusively designed for cricket fans at Axelerant and to engage them with the sport they love deeply." />
        <meta property="og:image" content="/logo.png" />
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://iplpredictionpro.axelerant.com/" />
        <meta property="twitter:title" content="IPL 2026 | Axelerant Prediction Pro" />
        <meta property="twitter:description" content="Axelerant IPL Prediction Pro is the prediction game for the T20 cricket league (Indian Premier League) exclusively designed for cricket fans at Axelerant and to engage them with the sport they love deeply." />
        <meta property="twitter:image" content="/logo.png" />
      </head>
      <body className={isImmersive ? 'immersive-route' : undefined}>
        <Analytics />
        {isWipMode ? (
          <WipPage />
        ) : isImmersive ? (
          // Bare landing — no header / nav / wrapper / footer. Whatever
          // the route renders takes the whole viewport.
          children
        ) : (
          <main className="main-page-wrapper">
            <div className="header">
              <div className="page-title-block">
                {/* Tournament label removed from header */}
                <SyncIndicator />
                <SunMoon />
                <AxelerantLogo colorMode={colorMode} />
                <PageTitle />
              </div>
            </div>
            <NavBar />
            {!online && (
              <OfflineBanner>
                App is operating in offline mode, data being displayed from local cache!
              </OfflineBanner>
            )}
            <div className="main-page-content">
              {children}
            </div>
            <div
              className="copyright has-tooltip"
              data-tooltip="We pre-filed — the 31st-century clerks were confused but compliant. Bought the 2000-year plan; the 1000-year felt short-sighted. You measure in years, we measure in ice ages. BTW Spoilers from 4026: Axelerant still runs the prediction game, now headquatered at Korolev, Mars. This footer is now a historical artifact. Please do not touch."
            >
              <div>Run by Axelerant bots with ❤️</div>
              <div>© {`402${new Date().getFullYear().toString().slice(-1)}`} Axelerant. All Rights Reserved.</div>
            </div>
          </main>
        )}
      </body>
    </html>
  )
}
